import { useEffect } from "react";

/** True if some element between `el` and `stopAt` can still scroll in the wheel's direction. */
function hasScrollableAncestor(el, stopAt, deltaY) {
  for (let node = el; node && node !== stopAt; node = node.parentElement) {
    if (node === document.body || node === document.documentElement) break;
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY !== "auto" && overflowY !== "scroll") continue;
    const canScrollDown = node.scrollTop + node.clientHeight < node.scrollHeight - 1;
    const canScrollUp = node.scrollTop > 0;
    if ((deltaY > 0 && canScrollDown) || (deltaY < 0 && canScrollUp)) return true;
  }
  return false;
}

/**
 * Wheel scrolling over `regionRef` (header, margins, tab strip) scrolls the element in
 * `scrollRef` — so the content scrolls even when the pointer is outside its own box.
 * Wheels outside the region (e.g. the left sidebar) are never touched. Inside the region
 * it also leaves alone wheels over the scroll element itself, an open modal, or another
 * element that can scroll itself (dropdown, details panel).
 */
export default function useForwardWheelScroll(regionRef, scrollRef) {
  useEffect(() => {
    const onWheel = (e) => {
      const region = regionRef.current;
      const target = scrollRef.current;
      if (!region || !target || e.ctrlKey || !e.deltaY) return;
      if (!region.contains(e.target) || target.contains(e.target)) return;
      if (document.body.classList.contains("modal-open") || e.target.closest?.(".modal")) return;
      if (hasScrollableAncestor(e.target, region, e.deltaY)) return;

      // deltaMode 1 = lines, 2 = pages; normalise to pixels.
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? target.clientHeight : 1;
      target.scrollTop += e.deltaY * unit;
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    return () => window.removeEventListener("wheel", onWheel);
  }, [regionRef, scrollRef]);
}
